/**
 * Input handling: keyboard, touch and pointer state for the canvas UI.
 *
 * Gamepad support was removed deliberately: a device only needs to be *attached*
 * to be picked up, and plenty of attached hardware is not a usable controller —
 * a Fanatec CSL pedal set exposes four axes all resting at -1, which read as
 * "steering held fully left" and silently overrode the keyboard forever.
 */

import { clamp, damp } from '@shared/math.js';
import type { VehicleInput } from '@shared/vehicle.js';
import { DEFAULT_CONTROLS, type KeyBindings } from '@shared/save.js';

export interface TouchControls {
  enabled: boolean;
  /** Steering: -1..1, driven by the on-screen stick. */
  steer: number;
  throttle: number;
  brake: number;
  handbrake: boolean;
}

export interface PointerState {
  x: number;
  y: number;
  down: boolean;
  pressed: boolean;
  released: boolean;
  wheel: number;
  inside: boolean;
}

export class InputManager {
  bindings: KeyBindings = { ...DEFAULT_CONTROLS };
  readonly pointer: PointerState = { x: 0, y: 0, down: false, pressed: false, released: false, wheel: 0, inside: false };

  /** Raw keyboard state; menus use the edge-triggered helpers below. */
  private keys = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private releasedThisFrame = new Set<string>();

  touch: TouchControls = { enabled: false, steer: 0, throttle: 0, brake: 0, handbrake: false };

  steeringSensitivity = 1;
  invertSteering = false;

  /** Set while a text field has focus so gameplay keys are ignored. */
  textEntryActive = false;

  private element: HTMLElement;
  private disposers: Array<() => void> = [];

  constructor(element: HTMLElement) {
    this.element = element;
    this.attach();
  }

  get isTouchDevice(): boolean {
    return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }

  private attach(): void {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (this.textEntryActive && e.code !== 'Escape') return;
      if (!this.keys.has(e.code)) this.pressedThisFrame.add(e.code);
      this.keys.add(e.code);
      if (
        !this.textEntryActive &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'Tab'].includes(e.code)
      ) {
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.keys.delete(e.code);
      this.releasedThisFrame.add(e.code);
    };
    const onBlur = (): void => {
      // Losing focus must never leave a key stuck down.
      this.keys.clear();
      this.touch.throttle = 0;
      this.touch.brake = 0;
      this.touch.steer = 0;
      this.touch.handbrake = false;
    };

    const onPointerMove = (e: PointerEvent): void => {
      // Straight element-space coordinates. The UI is drawn in exactly this
      // CSS-pixel space (the renderer takes its size from the same box), so no
      // scaling is applied — deriving one from a second measurement would go
      // wrong for a frame whenever the window is being resized.
      const rect = this.element.getBoundingClientRect();
      this.pointer.x = e.clientX - rect.left;
      this.pointer.y = e.clientY - rect.top;
      this.pointer.inside = true;
    };
    const onPointerDown = (e: PointerEvent): void => {
      onPointerMove(e);
      if (!this.pointer.down) this.pointer.pressed = true;
      this.pointer.down = true;
    };
    const onPointerUp = (e: PointerEvent): void => {
      onPointerMove(e);
      if (this.pointer.down) this.pointer.released = true;
      this.pointer.down = false;
    };
    const onPointerCancel = (): void => {
      if (this.pointer.down) this.pointer.released = true;
      this.pointer.down = false;
    };
    const onWheel = (e: WheelEvent): void => {
      this.pointer.wheel += e.deltaY;
      if (!this.textEntryActive) e.preventDefault();
    };
    const onContextMenu = (e: Event): void => e.preventDefault();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    this.element.addEventListener('pointermove', onPointerMove);
    this.element.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    this.element.addEventListener('wheel', onWheel, { passive: false });
    this.element.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('visibilitychange', onBlur);

    this.disposers.push(() => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      this.element.removeEventListener('pointermove', onPointerMove);
      this.element.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      this.element.removeEventListener('wheel', onWheel);
      this.element.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('visibilitychange', onBlur);
    });
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers = [];
  }

  setBindings(bindings: KeyBindings): void {
    this.bindings = { ...bindings };
  }

  /** Clears per-frame edge state. Call once at the end of every frame. */
  endFrame(): void {
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();
    this.pointer.pressed = false;
    this.pointer.released = false;
    this.pointer.wheel = 0;
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  wasPressed(code: string): boolean {
    return this.pressedThisFrame.has(code);
  }

  wasReleased(code: string): boolean {
    return this.releasedThisFrame.has(code);
  }

  anyPressed(codes: string[]): boolean {
    return codes.some((c) => this.pressedThisFrame.has(c));
  }

  /**
   * Combines every source into this frame's vehicle input. Steering is lightly
   * low-pass filtered: a raw keyboard jump from -1 to +1 would otherwise feel
   * like the wheels teleported.
   */
  sample(dt: number, previousSteer: number): { input: VehicleInput; reset: boolean; camera: boolean } {
    const b = this.bindings;
    let steer = 0;
    if (this.isDown(b.left)) steer -= 1;
    if (this.isDown(b.right)) steer += 1;
    let throttle = this.isDown(b.up) ? 1 : 0;
    let brake = this.isDown(b.down) ? 1 : 0;
    let handbrake = this.isDown(b.handbrake);
    let boost = this.isDown(b.boost);

    if (this.touch.enabled) {
      steer = clamp(steer + this.touch.steer, -1, 1);
      throttle = Math.max(throttle, this.touch.throttle);
      brake = Math.max(brake, this.touch.brake);
      handbrake = handbrake || this.touch.handbrake;
    }

    steer *= this.steeringSensitivity * (this.invertSteering ? -1 : 1);
    const smoothedSteer = clamp(previousSteer + (steer - previousSteer) * damp(26, dt), -1, 1);

    return {
      input: {
        throttle: clamp(throttle, 0, 1),
        brake: clamp(brake, 0, 1),
        steer: smoothedSteer,
        handbrake,
        boost,
      },
      reset: this.anyPressed([b.reset]),
      camera: this.anyPressed([b.camera]) || this.pointer.wheel !== 0,
    };
  }

  /** True when the player asked to pause (Escape). */
  wantsPause(): boolean {
    return this.anyPressed(['Escape']);
  }
}
